import React from 'react';
import { Typography, IconButton, Box, Chip, Tooltip } from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { format } from 'date-fns';
import { StationInfo } from '../types';

interface InfoCardProps {
  lastUpdated: Date;
  onChangeFavourites: () => void;
  stationInfo: StationInfo[] | null;
  crsCode?: string;
  isStale?: boolean;
}

const InfoCard: React.FC<InfoCardProps> = ({ lastUpdated, onChangeFavourites, stationInfo, crsCode, isStale }) => {
  const getInfoText = () => {
    if (crsCode) {
      return `Showing routes from ${crsCode}`;
    }
    if (!stationInfo) {
      return 'Loading station info...';
    }
    if (stationInfo.length === 0) {
      return 'No nearby stations found.';
    }

    const stationCRSs = stationInfo.map(s => s.crs).join(', ');
    const hasFavourite = stationInfo.some(s => s.is_favourite);

    if (hasFavourite) {
      const prefix = stationInfo.length > 1 ? 'Showing routes from nearby favourites' : 'Showing routes from nearby favourite';
      return `${prefix}: ${stationCRSs}`;
    }
    return `No favourites nearby, showing routes from closest station: ${stationCRSs}`;
  };

  return (
    <>
      <Tooltip title="Change favourite stations">
        <IconButton
          onClick={onChangeFavourites}
          aria-label="Settings"
          sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1000 }}
        >
          <SettingsIcon />
        </IconButton>
      </Tooltip>
      <Box sx={{ mt: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {getInfoText()}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 0.25 }}>
          <Typography variant="body2" color="text.secondary">
            Data last updated {format(lastUpdated, 'HH:mm')}
          </Typography>
          {isStale && (
            <Chip
              label="⚠️ Live updates paused (rate limit reached) – showing cached departures"
              size="small"
              color="warning"
              sx={{ fontSize: '0.75rem', height: 20 }}
            />
          )}
        </Box>
      </Box>
    </>
  );
};

export default InfoCard;
